import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { clearAppCachesAndReload } from '../utils/forceUpdate';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { ArrowLeft, SignOut, ArrowClockwise } from '@phosphor-icons/react';
import ClientsDirectorySection from '../components/ClientsDirectorySection';
import AppearanceSettings from '../components/AppearanceSettings';
import StatusManagement from '../components/StatusManagement';
import SettingsHub from '../components/SettingsHub';
import WithdrawReportModal from '../components/WithdrawReportModal';
import PendingManager from '../components/PendingManager';
import AppVersionCard from '../components/AppVersionCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * HandlerSettings — Session-37 rebuild.
 *
 * The handler three-dot menu is now hard-locked to Settings + Logout. Every
 * other capability funnels through here, gated by the 5 admin-controlled
 * flags: client_list, appearance, broadcast, status, reports. A fresh
 * handler with no grants sees an empty state.
 *
 * `reports` doesn't get a tile of its own — it silently unlocks Excel /
 * Summary / PDF inside every Group chat's three-dot menu.
 */
const HandlerSettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [perms, setPerms] = useState(() => { try { const _u = JSON.parse(localStorage.getItem('dws_user') || 'null'); return readInstantCache('handler_perms', _u?.id) || null; } catch { return null; } });
  const [loading, setLoading] = useState(() => { try { const _u = JSON.parse(localStorage.getItem('dws_user') || 'null'); return !readInstantCache('handler_perms', _u?.id); } catch { return true; } });
  // Session-47: page-level modal state so Reports card can pop the admin's modal
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [showPendingManager, setShowPendingManager] = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('dws_user');
    if (!u) { navigate('/handler-login'); return; }
    const parsed = JSON.parse(u);
    setUser(parsed);
    // Fetch fresh permissions from server (localStorage may be stale)
    axios.get(`${API}/users/handler/${parsed.id}/permissions`)
      .then((r) => { const _p = r.data?.permissions || {}; setPerms(_p); writeInstantCache('handler_perms', parsed.id, _p); })
      .catch(() => setPerms(parsed.permissions || {}))
      .finally(() => setLoading(false));
  }, [navigate]);

  if (!user) return null;

  const I = SettingsHub.icons;
  const isThirdParty = user.role === 'third_party_withdraw_handler';
  const isAdmin = user.role === 'super_admin';
  const allow = (key) => isAdmin || !!(perms || {})[key];

  // Build sections dynamically based on permissions
  const sections = [];
  // Pending Manager — ALWAYS available to every handler + admin. Explains &
  // clears every lit toggle (S/W/Deposit/Inquiry unread, PW/PT/N/G).
  sections.push({
    key: 'pending_manager', title: 'Pending Manager',
    subtitle: 'Dekho kaunse toggle (S/W/PW/PT/N/G) pending hain & clear karo',
    icon: I.Broom || I.ShieldCheck, accent: '#334155',
    render: () => (
      <div className="p-4">
        <button
          onClick={() => setShowPendingManager(true)}
          className="w-full py-2.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold"
          data-testid="handler-open-pending-manager-tile"
        >
          Open Pending Manager →
        </button>
        <p className="text-[11px] text-slate-500 mt-2 text-center">
          Har lit toggle ki detail, orphan pending, aur one-tap clear / mark-solved.
        </p>
      </div>
    ),
  });
  if (isThirdParty) {
    if (allow('appearance')) {
      sections.push({
        key: 'appearance', title: 'Appearance',
        subtitle: 'Theme (5 presets) and font size for this device',
        icon: I.PaintBrush, accent: '#7c3aed',
        render: () => <AppearanceSettings />,
      });
    }
  } else {
    // Session-37: 6-tile mapping. Each tile respects the matching admin
    // toggle from Handler Management → 🔑 Settings Powers.
    //   • `client_list`      → one-tap tile that jumps into Client Management
    //   • `client_directory` → inline Clients Directory section (search + open chats)
    // These are TWO DIFFERENT features per user requirement — a handler can
    // have either, both, or none.
    if (allow('client_list')) {
      sections.push({
        key: 'client_list', title: 'Client List',
        subtitle: 'Open the full Client Management screen (add / edit / delete)',
        icon: I.Users, accent: '#0ea5e9',
        render: () => (
          <div className="p-4">
            <button
              onClick={() => navigate('/client-list', { state: { from: '/handler-settings' } })}
              className="w-full py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold"
              data-testid="handler-open-client-management-btn"
            >
              Open Client Management →
            </button>
            <p className="text-[11px] text-slate-500 mt-2 text-center">
              Sabhi clients ka master list, add / edit / delete controls.
            </p>
          </div>
        ),
      });
    }
    if (allow('client_directory')) {
      sections.push({
        key: 'clients', title: 'Clients Directory',
        subtitle: 'View all clients by MB, message on WhatsApp, open chats',
        icon: I.UsersFour, accent: '#2563eb',
        render: () => <ClientsDirectorySection actorRole={user.role} permissions={perms} />,
      });
    }
    if (allow('create_client')) {
      sections.push({
        key: 'create_client', title: 'Create Client',
        subtitle: 'Add a new customer account (same flow as admin)',
        icon: I.IdentificationCard, accent: '#0d9488',
        render: () => (
          <div className="p-4">
            <button
              onClick={() => navigate('/handler/create-client', { state: { from: '/handler-settings' } })}
              className="w-full py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold"
              data-testid="handler-open-create-client-btn"
            >
              Open Create Client →
            </button>
            <p className="text-[11px] text-slate-500 mt-2 text-center">
              Naya customer account banane ka poora form.
            </p>
          </div>
        ),
      });
    }
    if (allow('status')) {
      sections.push({
        key: 'status', title: 'My Status',
        subtitle: 'Update your handler activity status',
        icon: I.PaintBrush, accent: '#16a34a',
        render: () => <StatusManagement user={user} />,
      });
    }
    if (allow('appearance')) {
      sections.push({
        key: 'appearance', title: 'Appearance',
        subtitle: 'Theme (5 presets) and font size for this device',
        icon: I.PaintBrush, accent: '#7c3aed',
        render: () => <AppearanceSettings />,
      });
    }
    if (allow('broadcast')) {
      sections.push({
        key: 'broadcast', title: 'Broadcast Message',
        subtitle: 'Push a single message to every customer or a chosen MB',
        icon: I.MegaphoneSimple, accent: '#ea580c',
        render: () => (
          <div className="p-4">
            <button
              onClick={() => navigate('/broadcast', { state: { from: '/handler-settings' } })}
              className="w-full py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold"
              data-testid="handler-open-broadcast-btn"
            >
              Open Broadcast Composer →
            </button>
            <p className="text-[11px] text-slate-500 mt-2 text-center">
              Broadcast page will open with your granted MB scopes.
            </p>
          </div>
        ),
      });
    }
    if (allow('reports')) {
      sections.push({
        key: 'reports', title: 'Deposit / Withdraw Reports',
        subtitle: 'Excel · PDF · MB-wise (same as admin)',
        icon: I.ShieldCheck, accent: '#0891b2',
        // Session-47: launch the FULL admin Reports UI — same modal that
        // admins use from AdminSettings. No more "Open Groups" redirect.
        render: () => (
          <div className="p-3">
            <button
              onClick={() => setShowReportsModal(true)}
              className="w-full py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
              data-testid="handler-open-reports-btn"
            >Open Reports →</button>
            <p className="text-[11px] text-slate-500 mt-2 text-center">
              Excel · PDF · MB-wise · Date filters — everything admin can do.
            </p>
          </div>
        ),
      });
    }
    if (allow('service_timeline')) {
      sections.push({
        key: 'service_timeline', title: 'Service Timelines',
        subtitle: 'Track deposit / withdraw / support requests & timings',
        icon: I.PaintBrush, accent: '#0891b2',
        render: () => (
          <div className="p-4">
            <button
              onClick={() => navigate('/handler/service-timelines')}
              className="w-full py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
              data-testid="handler-open-service-timelines-btn"
            >
              Open Service Timelines →
            </button>
            <p className="text-[11px] text-slate-500 mt-2 text-center">
              Request numbers · Started / Accepted / Closed · Time Between.
            </p>
          </div>
        ),
      });
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('dws_user');
    localStorage.removeItem('dws_token');
    sessionStorage.clear();
    navigate('/handler-login');
  };

  return (
    <div className="h-screen overflow-y-auto bg-slate-50" data-testid="handler-settings-page">
      <div className="bg-white shadow-sm px-4 py-3 flex items-center gap-2 sticky top-0 z-10 border-b border-slate-200">
        <button onClick={() => {
          // Fix doc #2 — let an open drill-down section step back one internal level first.
          const ev = new CustomEvent('dws:settings-back', { cancelable: true });
          window.dispatchEvent(ev);
          if (ev.defaultPrevented) return;
          if (window.history.length > 1) navigate(-1); else navigate('/handler-dashboard');
        }} className="p-2 hover:bg-slate-100 rounded-full" data-testid="handler-settings-back">
          <ArrowLeft size={22} />
        </button>
        <h1 className="text-lg font-bold text-slate-800 flex-1">Settings</h1>
        <button
          onClick={() => clearAppCachesAndReload()}
          className="px-3 py-1.5 mr-2 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded flex items-center gap-1"
          data-testid="handler-force-update-btn"
          title="Load the latest app version (no logout)"
        >
          <ArrowClockwise size={14} weight="bold" /> Force Update
        </button>
        <button
          onClick={handleLogout}
          className="px-3 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-1"
          data-testid="handler-logout-btn"
        >
          <SignOut size={14} weight="bold" /> Logout
        </button>
      </div>
      <div className="max-w-5xl mx-auto p-4">
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading settings…</div>
        ) : sections.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="text-4xl mb-2">🔒</div>
            <div className="text-sm font-bold text-slate-700 mb-1">No Settings Access</div>
            <div className="text-[11px] text-slate-500 px-6">Admin ne aapko koi setting access nahi diya. Admin se contact karein.</div>
          </div>
        ) : (
          <SettingsHub sections={sections} />
        )}
        <div className="mt-4">
          <AppVersionCard />
        </div>
      </div>
      {/* Session-47: Reports modal — same one AdminSettings uses */}
      {showReportsModal && (
        <WithdrawReportModal
          open={true}
          onClose={() => setShowReportsModal(false)}
          actorRole={user?.role || 'handler'}
        />
      )}
      {showPendingManager && (
        <PendingManager user={user} onClose={() => setShowPendingManager(false)} />
      )}
    </div>
  );
};

export default HandlerSettings;
